import { useEffect, useState } from "react";
import { Loader2, Save, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { CHATBOT_PROGRAMS } from "@/lib/chatbotMeta";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const EMBED = `<script\n  src="${ORIGIN}/chatbot-widget.js"\n  data-branch="iec-mustikajaya"\n  data-position="bottom-left"\n  defer>\n</script>`;

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const PROGRAM_KEYS = ["efk", "eyl", "efj", "ge", "toefl", "toeic", "ielts", "school", "corporate", "private"];
const MENU_LABELS = {
  anak: "Program untuk Anak", remaja: "Program untuk Remaja", dewasa: "Program untuk Dewasa",
  tes: "TOEFL/TOEIC/IELTS", sekolah: "Program Sekolah", perusahaan: "Program Perusahaan",
  daftar: "Daftar / Hubungi Admin",
};
const MENU_KEYS = ["anak", "remaja", "dewasa", "tes", "sekolah", "perusahaan", "daftar"];

function TF({ label, value, onChange, area, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 mb-1 block">{label}</label>
      {area
        ? <textarea className={inputCls} rows={2} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} />
        : <input className={inputCls} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} />}
    </div>
  );
}

export default function ChatbotSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/chatbot/settings").then(({ data }) => setS(data)).catch(() => toast.error("Gagal memuat pengaturan.")).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
  const setPd = (k) => (e) => setS((p) => ({ ...p, program_descriptions: { ...p.program_descriptions, [k]: e.target.value } }));
  const setMa = (k) => (e) => setS((p) => ({ ...p, menu_answers: { ...p.menu_answers, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/chatbot/settings", s);
      setS(data);
      toast.success("Pengaturan chatbot disimpan.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <AppLayout><div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pengaturan Chatbot</h1>
          <p className="text-sm text-slate-500">Semua konten chatbot publik dapat diedit di sini</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/chatbot" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm border border-slate-300 text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid="open-chatbot"><ExternalLink className="h-4 w-4" /> Lihat Chatbot</a>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0f2847] hover:bg-[#17416f] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="save-settings">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <h2 className="font-semibold text-slate-900">Umum & Pesan</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.is_active} onChange={(e) => setS((p) => ({ ...p, is_active: e.target.checked }))} data-testid="setting-active" /> Chatbot Aktif
          </label>
          <TF label="Pesan Sambutan" area value={s.welcome_message} onChange={set("welcome_message")} testid="setting-welcome" />
          <TF label="Pesan Bantuan" area value={s.help_message} onChange={set("help_message")} testid="setting-help" />
          <TF label="Pesan Penutup" area value={s.closing_message} onChange={set("closing_message")} testid="setting-closing" />
          <TF label="Metode Pembelajaran" area value={s.method_info} onChange={set("method_info")} testid="setting-method" />
          <TF label="Informasi Jadwal Umum" area value={s.schedule_info} onChange={set("schedule_info")} testid="setting-schedule" placeholder="Kosongkan jika belum tersedia" />
          <TF label="Informasi Biaya Umum" area value={s.cost_info} onChange={set("cost_info")} testid="setting-cost" placeholder="Kosongkan jika belum tersedia" />
          <TF label="Alamat / Lokasi" area value={s.address} onChange={set("address")} testid="setting-address" />
          <TF label="Link Google Maps" value={s.maps_link} onChange={set("maps_link")} testid="setting-maps" placeholder="https://..." />
          <TF label="Nomor WhatsApp Admin" value={s.whatsapp} onChange={set("whatsapp")} testid="setting-whatsapp" placeholder="08xxxxxxxxxx" />
          <TF label="Jam Operasional" value={s.operational_hours} onChange={set("operational_hours")} testid="setting-hours" />
          <TF label="Pesan di Luar Jam Operasional" area value={s.offhours_message} onChange={set("offhours_message")} testid="setting-offhours" />
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h2 className="font-semibold text-slate-900">Formulir Pendaftaran</h2>
            <TF label="Pesan Sebelum Formulir" area value={s.form_intro} onChange={set("form_intro")} testid="setting-form-intro" />
            <TF label="Pesan Persetujuan" area value={s.consent_text} onChange={set("consent_text")} testid="setting-consent" />
            <TF label="Link Formulir Pendaftaran" value={s.registration_link} onChange={set("registration_link")} testid="setting-registration" placeholder="https://..." />
            <TF label="Pesan Berhasil Dikirim" area value={s.success_message} onChange={set("success_message")} testid="setting-success" />
            <TF label="Pesan Nomor Sudah Terdaftar" area value={s.duplicate_message} onChange={set("duplicate_message")} testid="setting-duplicate" />
            <TF label="Pesan Gagal" area value={s.error_message} onChange={set("error_message")} testid="setting-error" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h2 className="font-semibold text-slate-900">Jawaban Menu Cepat</h2>
            {MENU_KEYS.map((k) => (
              <TF key={k} label={MENU_LABELS[k]} area value={s.menu_answers?.[k] || ""} onChange={setMa(k)} testid={`setting-menu-${k}`} />
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h2 className="font-semibold text-slate-900">Deskripsi Program</h2>
            {PROGRAM_KEYS.map((k) => (
              <TF key={k} label={CHATBOT_PROGRAMS[k].label} area value={s.program_descriptions?.[k] || ""} onChange={setPd(k)} testid={`setting-prog-${k}`} placeholder="Kosongkan jika belum tersedia" />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="widget-settings">
          <h2 className="font-semibold text-slate-900">Pengaturan Widget</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.widget_active} onChange={(e) => setS((p) => ({ ...p, widget_active: e.target.checked }))} data-testid="widget-active" /> Widget Aktif
          </label>
          <TF label="Judul Widget" value={s.widget_title} onChange={set("widget_title")} testid="widget-title" />
          <TF label="Teks Tombol" value={s.widget_button_text} onChange={set("widget_button_text")} testid="widget-button-text" />
          <TF label="Pesan Pembuka Singkat" area value={s.widget_open_message} onChange={set("widget_open_message")} testid="widget-open-message" />
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Posisi</label>
            <select className={inputCls} value={s.widget_position} onChange={set("widget_position")} data-testid="widget-position">
              <option value="bottom-right">Kanan Bawah</option>
              <option value="bottom-left">Kiri Bawah</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Warna Utama</label>
            <input type="color" className="h-10 w-20 rounded border border-slate-300" value={s.widget_color || "#0f2847"} onChange={set("widget_color")} data-testid="widget-color" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Domain yang Diizinkan (satu per baris)</label>
            <textarea className={inputCls} rows={3} value={(s.widget_allowed_domains || []).join("\n")} onChange={(e) => setS((p) => ({ ...p, widget_allowed_domains: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) }))} data-testid="widget-domains" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.widget_branding} onChange={(e) => setS((p) => ({ ...p, widget_branding: e.target.checked }))} data-testid="widget-branding" /> Tampilkan Branding IEC
          </label>
          <TF label="Delay Kemunculan Tombol (detik)" value={s.widget_delay ?? 0} onChange={(e) => setS((p) => ({ ...p, widget_delay: parseInt(e.target.value, 10) || 0 }))} testid="widget-delay" />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Pasang di WordPress</h2>
            <div className="flex gap-2">
              <a href="/widget-demo" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-700 px-2.5 py-1.5 rounded-lg hover:bg-slate-50" data-testid="open-widget-demo"><ExternalLink className="h-3.5 w-3.5" /> Lihat Demo</a>
              <button onClick={() => { navigator.clipboard.writeText(EMBED); toast.success("Kode embed disalin."); }} className="inline-flex items-center gap-1.5 text-xs bg-[#0f2847] text-white px-2.5 py-1.5 rounded-lg" data-testid="copy-embed"><Copy className="h-3.5 w-3.5" /> Salin Kode Embed</button>
            </div>
          </div>
          <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap" data-testid="embed-code">{EMBED}</pre>
          <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
            <li>Salin kode embed di atas.</li>
            <li>Login ke dashboard WordPress iecmustikajaya.id.</li>
            <li>Pasang melalui Custom HTML, plugin header/footer, atau area script global.</li>
            <li>Simpan, lalu buka website dalam mode incognito.</li>
            <li>Periksa widget pada desktop dan HP.</li>
            <li>Jika memakai cache WordPress/Cloudflare, bersihkan cache setelah perubahan.</li>
          </ol>
        </div>
      </div>
    </AppLayout>
  );
}
